import React, { useContext, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../contexts/UserContext";
import { usePro } from "../contexts/ProContext";
import { formatSubscriptionDate } from "../utils/subscription";
import { createStripeCheckoutSession } from "../integrations/APIClient";

function createPlans() {
  return {
    yearly: {
      key: "yearly",
      title: "Chesshood Pro - Yearly",
      subtitle: "12 months • auto-renewable",
      displayPrice: process.env.REACT_APP_STRIPE_YEARLY_DISPLAY || "Price shown at checkout",
      foot:
        process.env.REACT_APP_STRIPE_YEARLY_FOOT ||
        "Billed once a year",
      badge: "Best value",
    },
    monthly: {
      key: "monthly",
      title: "Chesshood Pro - Monthly",
      subtitle: "1 month • auto-renewable",
      displayPrice: process.env.REACT_APP_STRIPE_MONTHLY_DISPLAY || "Price shown at checkout",
      foot:
        process.env.REACT_APP_STRIPE_MONTHLY_FOOT ||
        "Pay month to month • auto-renewable",
    },
  };
}

export default function Checkout() {
  const { isPro, subscription, refresh } = usePro();
  const { firebaseUser, loading, profileLoading } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const fromHood = new URLSearchParams(location.search).get("returnTo") === "hoodai";
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedPlanKey, setSelectedPlanKey] = useState("yearly");

  const plans = useMemo(() => createPlans(), []);
  const selectedPlan = plans[selectedPlanKey];

  const checkoutConfig = useMemo(
    () => ({
      successUrl: `${window.location.origin}${fromHood ? "/hood-ai?pro=return" : "/success"}`,
      cancelUrl: `${window.location.origin}${fromHood ? "/hood-ai?pro=cancelled" : "/cancel"}`,
    }),
    [fromHood]
  );

  async function redirectToCheckout() {
    if (busy || loading || profileLoading || isPro) return;
    if (!firebaseUser) {
      navigate("/login", { state: { from: { pathname: fromHood ? "/checkout?returnTo=hoodai" : "/checkout" } } });
      return;
    }
    setMessage("");
    setBusy(true);

    try {
      const session = await createStripeCheckoutSession({
        plan: selectedPlan?.key || selectedPlanKey,
        successUrl: checkoutConfig.successUrl,
        cancelUrl: checkoutConfig.cancelUrl,
      });

      if (!session?.url) {
        throw new Error("Backend did not return a Stripe checkout URL.");
      }

      const destination = new URL(session.url);
      if (destination.protocol !== "https:" || destination.hostname !== "checkout.stripe.com") {
        throw new Error("The payment link could not be verified. Please try again.");
      }
      window.location.assign(destination.href);
    } catch (error) {
      setMessage(error?.message || "Stripe checkout could not start.");
    } finally {
      setBusy(false);
    }
  }

  async function refreshStatus() {
    setBusy(true);
    setMessage("");

    try {
      await refresh();
      setMessage("Your Pro access has been checked.");
    } catch (error) {
      setMessage(error?.message || "Could not check your Pro access.");
    } finally {
      setBusy(false);
    }
  }

  const expiresLabel = formatSubscriptionDate(subscription?.expiresAt);
  const benefits = [
    "No ads",
    "All categories",
    "Faster loading",
    "Priority support",
  ];

  return (
    <div
      style={{
        maxWidth: 1120,
        margin: "0 auto",
        color: "#f2ede2",
      }}
    >
      <style>{`
        .checkout-shell {
          background: linear-gradient(180deg, #131313, #090909);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 26px;
          overflow: hidden;
          box-shadow: 0 24px 50px rgba(0,0,0,0.32);
        }
        .checkout-hero {
          padding: 26px 22px 18px;
          background:
            radial-gradient(circle at top right, rgba(242,184,77,0.2), transparent 34%),
            linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0));
        }
        .checkout-kicker {
          color: #f0b454;
          font-size: 0.78rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .checkout-title {
          margin: 8px 0 10px;
          color: #fff6de;
          font-size: clamp(1.9rem, 4vw, 2.8rem);
          font-weight: 900;
          line-height: 1.05;
        }
        .checkout-copy {
          max-width: 58ch;
          margin: 0;
          color: #ddd3c2;
          line-height: 1.6;
        }
        .checkout-grid {
          display: grid;
          grid-template-columns: minmax(280px, 0.95fr) minmax(380px, 1.25fr);
          gap: 16px;
          padding: 0 22px 22px;
        }
        .checkout-card {
          padding: 18px;
          border-radius: 20px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
        }
        .checkout-card h2 {
          margin: 0 0 8px;
          color: #fff7e3;
          font-size: 1.2rem;
          font-weight: 800;
        }
        .checkout-status-pill {
          display: inline-flex;
          align-items: center;
          padding: 8px 12px;
          border-radius: 999px;
          background: ${isPro ? "rgba(255,215,97,0.14)" : "rgba(255,255,255,0.06)"};
          color: ${isPro ? "#ffd761" : "#f2ebda"};
          font-weight: 800;
        }
        .checkout-card p,
        .checkout-card li {
          color: #d8ccb4;
          line-height: 1.55;
        }
        .checkout-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 14px;
        }
        .checkout-button,
        .checkout-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          padding: 0 16px;
          border-radius: 14px;
          border: 0;
          text-decoration: none;
          font-weight: 900;
        }
        .checkout-button-primary {
          background: linear-gradient(180deg, #f2b84d, #da8b26);
          color: #1b1204;
        }
        .checkout-button-secondary {
          background: rgba(255,255,255,0.08);
          color: #f6eedb;
        }
        .checkout-message {
          margin-top: 12px;
          color: #f4d078;
        }
        .checkout-benefits {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          margin: 16px 0 14px;
        }
        .checkout-benefit {
          padding: 10px 12px;
          border-radius: 14px;
          background: rgba(0,0,0,0.28);
          color: #f5ecda;
          font-weight: 700;
        }
        .checkout-plan {
          margin-top: 10px;
          padding: 16px;
          border-radius: 18px;
          border: 1px solid rgba(255,255,255,0.12);
          background: rgba(255,255,255,0.04);
          cursor: pointer;
        }
        .checkout-plan-active {
          background: rgba(242,184,77,0.14);
          border-color: #f2b84d;
        }
        .checkout-plan-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }
        .checkout-plan-title {
          color: #fff6df;
          font-size: 1.06rem;
          font-weight: 900;
        }
        .checkout-plan-badge {
          padding: 4px 10px;
          border-radius: 999px;
          background: #f2b84d;
          color: #18110a;
          font-size: 0.76rem;
          font-weight: 900;
        }
        .checkout-plan-subtitle {
          margin-top: 6px;
          color: #d7c7a9;
        }
        .checkout-plan-price {
          margin-top: 10px;
          color: #ffffff;
          font-size: 1.35rem;
          font-weight: 900;
        }
        .checkout-plan-foot {
          margin-top: 6px;
          color: #f2d180;
          font-size: 0.92rem;
        }
        .checkout-legal {
          margin-top: 14px;
          color: #baa98a;
          font-size: 0.92rem;
          line-height: 1.55;
        }
        .checkout-links {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 10px;
        }
        .checkout-links a {
          color: #fff4d6;
        }
        @media (max-width: 900px) {
          .checkout-grid {
            grid-template-columns: 1fr;
          }
          .checkout-benefits {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="checkout-shell">
        <div className="checkout-hero">
          <div className="checkout-kicker">Chesshood Pro</div>
          <h1 className="checkout-title">
            Learn with Chesshood Pro
          </h1>
          <p className="checkout-copy">
            Choose monthly or yearly Pro access. Keep learning with Hood and
            unlock Pro features on your Chesshood account.
          </p>
        </div>

        <div className="checkout-grid">
          <section className="checkout-card">
            <h2>Your current plan</h2>
            <div className="checkout-status-pill">
              {isPro ? "Pro active" : "Free plan"}
            </div>

            <p>
              {expiresLabel
                ? `Valid until ${expiresLabel}.`
                : isPro
                  ? "Pro is active on your account."
                  : "Choose Pro whenever you are ready."}
            </p>

            <div className="checkout-benefits">
              {benefits.map((benefit) => (
                <div key={benefit} className="checkout-benefit">
                  {benefit}
                </div>
              ))}
            </div>

            <div className="checkout-actions">
              <button
                type="button"
                className="checkout-button checkout-button-secondary"
                onClick={refreshStatus}
                disabled={busy}
              >
                Refresh status
              </button>
              <Link to="/profile" className="checkout-link checkout-button-secondary">
                Open profile
              </Link>
            </div>
            {message ? <div className="checkout-message">{message}</div> : null}
          </section>

          <section className="checkout-card">
            <h2>Choose your plan</h2>
            <p>
              Choose how often you pay. Pro renews automatically until cancelled.
              Stripe shows the exact price, currency and any applicable tax before you pay.
            </p>

            {Object.values(plans).map((plan) => (
              <button
                key={plan.key}
                type="button"
                className={`checkout-plan ${
                  selectedPlanKey === plan.key ? "checkout-plan-active" : ""
                }`}
                onClick={() => setSelectedPlanKey(plan.key)}
              >
                <div className="checkout-plan-head">
                  <div className="checkout-plan-title">{plan.title}</div>
                  {plan.badge ? (
                    <div className="checkout-plan-badge">{plan.badge}</div>
                  ) : null}
                </div>
                <div className="checkout-plan-subtitle">{plan.subtitle}</div>
                <div className="checkout-plan-price">{plan.displayPrice}</div>
                <div className="checkout-plan-foot">{plan.foot}</div>
              </button>
            ))}

            <div className="checkout-actions">
              <button
                type="button"
                className="checkout-button checkout-button-primary"
                onClick={redirectToCheckout}
                disabled={busy || loading || profileLoading || isPro}
              >
                {isPro ? "Pro is active" : !firebaseUser ? "Sign in for Pro" : busy ? "Opening Stripe…" : selectedPlanKey === "yearly"
                  ? "Get yearly Pro"
                  : "Get monthly Pro"}
              </button>
              <Link to={fromHood ? "/hood-ai" : "/play"} className="checkout-link checkout-button-secondary">
                Back to training
              </Link>
            </div>

            <div className="checkout-legal">
              Payments are handled securely by Stripe. Review the recurring price
              and billing terms before confirming. Already have Pro? Sign in with
              the same Chesshood account to use your existing access.
              <div className="checkout-links">
                <a href="/privacy">Privacy Policy</a>
                <a href="/subscription-terms">Pro Terms</a>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

